import { type CSSProperties, useState } from "react";
import { ArrowRight, Lock, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { SectionHeading } from "@/components/sections/SectionHeading";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { StronomModule, stronomModules } from "@/data/stronom-modules";

export const ModulesSection = () => {
  const navigate = useNavigate();
  const [selectedModule, setSelectedModule] = useState<StronomModule | null>(null);

  const handleCardClick = (module: StronomModule) => {
    if (module.status === "coming_soon") {
      setSelectedModule(module);
      return;
    }

    if (module.status === "foundation") {
      navigate(module.slug);
    }
  };

  const closeDialog = () => setSelectedModule(null);

  return (
    <section id="suite" className="relative py-28">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/12 to-transparent" />
      <div className="mx-auto max-w-7xl px-6">
        <SectionHeading
          eyebrow="The STRONOM Suite"
          title="Eight domains unified by proof-based intelligence"
          description="Every module anchors to STRONOM Core. Design is live today; the remaining domains are staged for controlled releases and partner onboarding."
          align="center"
        />

        <div className="mt-16 grid gap-6 md:grid-cols-2 xl:grid-cols-4">
          {stronomModules.map((module) => {
            const accentColor = `hsl(var(${module.accentVar}))`;
            // ✅ Fix: cast custom CSS var to a style object so TS accepts it
            const accentStyle = {
              ["--module-accent" as any]: accentColor,
            } as CSSProperties;

            return (
              <div
                key={module.key}
                style={accentStyle}
                role="button"
                tabIndex={0}
                onClick={() => handleCardClick(module)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    handleCardClick(module);
                  }
                }}
                className={cn(
                  "group relative flex h-full flex-col items-start gap-6 rounded-3xl border border-border/70 bg-card/80 p-8 text-left transition-all",
                  module.status === "active" ? "hover:-translate-y-1.5" : "hover:-translate-y-1",
                  module.status === "coming_soon" && "cursor-pointer",
                  module.status === "foundation" && "cursor-pointer",
                )}
              >
                <div className="absolute inset-0 rounded-3xl border border-white/5 shadow-[0_32px_120px_rgba(4,8,16,0.55)]" />
                <div
                  className="absolute inset-0 rounded-3xl opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                  style={{
                    background:
                      "radial-gradient(135% 115% at 20% 0%, var(--module-accent) 0%, transparent 68%)",
                  }}
                />
                <div
                  className="absolute inset-0 rounded-3xl border border-transparent opacity-0 transition-all duration-300 group-hover:opacity-100"
                  style={{
                    borderColor: accentColor,
                    boxShadow: "0 40px 120px -50px var(--module-accent)",
                  }}
                />

                <div className="relative flex items-center gap-4">
                  <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-black/40 ring-1 ring-inset ring-white/10">
                    <img src={module.icon} alt={module.title} className="h-10 w-10" loading="lazy" />
                  </span>
                  <div className="space-y-1">
                    <p className="text-xs font-semibold uppercase tracking-[0.35em] text-stronom-design/65">
                      {module.statusLabel}
                    </p>
                    <h3 className="font-display text-2xl font-semibold text-foreground">{module.title}</h3>
                  </div>
                </div>

                <p className="relative text-sm text-muted-foreground">{module.description}</p>

                <div className="relative mt-auto w-full">
                  {module.status === "active" && module.ctaLink ? (
                    <Button
                      onClick={(event) => {
                        event.stopPropagation();
                        navigate(module.ctaLink!);
                      }}
                      className="w-full shadow-[0_20px_60px_rgba(0,179,255,0.35)]"
                    >
                      {module.ctaLabel}
                      <ArrowRight className="ml-2 size-4" />
                    </Button>
                  ) : module.status === "foundation" ? (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Sparkles className="size-4 text-stronom-agents/80" />
                      Governed by STRONOM Core
                    </div>
                  ) : (
                    <Button
                      variant="outline"
                      className="w-full border-dashed border-border/70 text-muted-foreground"
                      onClick={(event) => {
                        event.stopPropagation();
                        setSelectedModule(module);
                      }}
                    >
                      Coming Soon
                      <Lock className="ml-2 size-4" />
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <Dialog open={Boolean(selectedModule)} onOpenChange={(open) => (open ? null : closeDialog())}>
        <DialogContent className="border border-white/10 bg-black/80 backdrop-blur">
          <DialogHeader>
            <DialogTitle className="text-lg text-foreground">
              {selectedModule?.title ?? "Module in development"}
            </DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">
              We are onboarding strategic partners before opening this module. Register interest to receive launch
              timelines and integration previews.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex flex-col gap-3 sm:flex-row sm:justify-end">
            <Button variant="ghost" onClick={closeDialog} className="text-muted-foreground">
              Close
            </Button>
            <Button asChild className="shadow-[0_18px_50px_rgba(0,179,255,0.35)]">
              <a href="mailto:contact@stronom.com?subject=STRONOM%20Suite%20Access">Request Partnership</a>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
};
